import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { CopyButton } from "design-system/organisms/chat/CopyButton"
import { getErrorTranslations } from "lib/i18n"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { useCompanyQuery } from "modules/settings/hooks/useCompanyQuery"
import { useBigBlueIntegrationQuery } from "modules/settings/hooks/bigblue/useBigBlueIntegrationQuery"
import { useUpsertBigBlueIntegrationMutation } from "modules/settings/hooks/bigblue/useUpsertBigBlueIntegrationMutation"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertBigBlueIntegrationSchema } from "schema"
import { z } from "zod"
import config from "lib/config"
import { DeleteBigBlueIntegration } from "./DeleteBigBlueIntegration"

export const BigBlueAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertBigBlueIntegrationSchema>>({
    resolver: zodResolver(UpsertBigBlueIntegrationSchema),
  })
  const navigate = useNavigate()
  const { mutateAsync } = useUpsertBigBlueIntegrationMutation()
  const { data } = useBigBlueIntegrationQuery()
  const { data: company } = useCompanyQuery()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const webhookUrl = company?.slug
    ? `${config.apiEndpoint}/webhooks/bigblue/${company.slug}/order-status`
    : undefined

  const onSubmit = async (data: z.infer<typeof UpsertBigBlueIntegrationSchema>) => {
    await mutateAsync({
      ...data,
      webhooksSecretKey: data.webhooksSecretKey || undefined,
    })
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/bigblue" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset({
        token: data.token ?? "",
        prefix: data.prefix ?? "",
        webhooksSecretKey: data.webhooksSecretKey ?? "",
      })
    }
  }, [data, form])

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.integrations.bigblue.authModal.title")}</DialogTitle>
            <DialogDescription>
              {t("settings.integrations.bigblue.authModal.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Form
              {...form}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
              errorTranslations={getErrorTranslations()}
            >
              <div className="flex flex-col w-full gap-4">
                <FormField
                  control={form.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.bigblue.authModal.form.token")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="prefix"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.bigblue.authModal.form.prefix")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="webhooksSecretKey"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.bigblue.authModal.form.webhooksSecretKey")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {webhookUrl ? (
                  <div className="text-sm text-muted-foreground">
                    <p>{t("settings.integrations.bigblue.authModal.form.webhookUrlLabel")}</p>
                    <div className="mt-1 flex items-start gap-1">
                      <p className="min-w-0 flex-1 break-all font-mono">{webhookUrl}</p>
                      <CopyButton
                        content={webhookUrl}
                        copyMessage={t(
                          "settings.integrations.bigblue.authModal.form.webhookUrlCopySuccess",
                        )}
                        errorMessage={t(
                          "settings.integrations.bigblue.authModal.form.webhookUrlCopyError",
                        )}
                        positioning={false}
                        className="shrink-0"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              <DialogFooter>
                {data?.token ? (
                  <AdminOnly superadminOnly>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      {t("settings.integrations.bigblue.integrationCard.deleteIntegration.button")}
                    </Button>
                  </AdminOnly>
                ) : null}
                <Button disabled={form.formState.isLoading || !form.formState.isValid}>
                  {t("settings.integrations.bigblue.authModal.form.submit")}
                </Button>
              </DialogFooter>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
      <DeleteBigBlueIntegration
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDeleted={() => onOpenChange()}
      />
    </>
  )
}
